// Firebase service for tracking user forum statistics including mentions
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  increment,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase-config";

/**
 * Update user forum statistics
 * @param {string} userId - User ID
 * @param {Object} updates - Statistics to update
 * @returns {Promise<Object>} - Success status
 */
export const updateUserForumStats = async (userId, updates) => {
  try {
    if (!userId || !updates) {
      return { success: false, error: 'User ID and updates are required' };
    }
    
    const statsRef = doc(db, 'user_forum_stats', userId);
    const statsSnap = await getDoc(statsRef);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    if (statsSnap.exists()) {
      // Update existing stats
      await updateDoc(statsRef, updateData);
    } else {
      // Create new stats document with defaults
      const defaultStats = {
        userId,
        questionsPosted: 0,
        repliesPosted: 0,
        totalLikesReceived: 0,
        totalViewsReceived: 0,
        mentionsReceived: 0,
        mentionsGiven: 0,
        createdAt: serverTimestamp(),
        ...updateData
      };
      
      await setDoc(statsRef, defaultStats);
    }
    
    return { success: true };
    
  } catch (error) {
    console.error('Error updating user forum stats:', error);
    return {
      success: false,
      error: error.message || 'Failed to update forum stats'
    };
  }
};

/**
 * Get user forum statistics
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User statistics
 */
export const getUserForumStats = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }
    
    const statsRef = doc(db, 'user_forum_stats', userId);
    const statsSnap = await getDoc(statsRef);
    
    if (!statsSnap.exists()) {
      // Return default stats if none exist
      const defaultStats = {
        userId,
        questionsPosted: 0,
        repliesPosted: 0,
        totalLikesReceived: 0,
        totalViewsReceived: 0,
        mentionsReceived: 0,
        mentionsGiven: 0,
        createdAt: null,
        updatedAt: null
      };
      
      return {
        success: true,
        data: defaultStats
      };
    }
    
    return {
      success: true,
      data: {
        userId,
        ...statsSnap.data()
      }
    };
    
  } catch (error) {
    console.error('Error fetching user forum stats:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch forum stats',
      data: null
    };
  }
};

/**
 * Increment mention statistics for users
 * @param {string} mentionerUserId - User who made the mention
 * @param {Array} mentionedUserIds - Users who were mentioned
 * @returns {Promise<Object>} - Success status
 */
export const updateMentionStats = async (mentionerUserId, mentionedUserIds) => {
  try {
    if (!mentionerUserId || !mentionedUserIds || mentionedUserIds.length === 0) {
      return { success: true }; // Nothing to update
    }
    
    // Update stats for the user who made mentions
    await updateUserForumStats(mentionerUserId, {
      mentionsGiven: increment(mentionedUserIds.length)
    });
    
    // Update stats for each mentioned user
    const updatePromises = mentionedUserIds.map(userId => 
      updateUserForumStats(userId, {
        mentionsReceived: increment(1)
      })
    );
    
    await Promise.all(updatePromises);
    
    console.log(`Updated mention stats: ${mentionerUserId} mentioned ${mentionedUserIds.length} users`);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error updating mention stats:', error);
    return {
      success: false,
      error: error.message || 'Failed to update mention stats'
    };
  }
};

/**
 * Get mention statistics for moderation
 * @param {string} userId - User ID to check
 * @param {number} days - Number of days to look back
 * @returns {Promise<Object>} - Mention statistics
 */
export const getMentionModerationStats = async (userId, days = 7) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }
    
    // Get overall stats
    const statsResult = await getUserForumStats(userId);
    if (!statsResult.success) {
      return statsResult;
    }
    
    // TODO: Implement time-based mention tracking for moderation
    // This would require additional collections to track mentions over time
    // For now, return basic stats
    
    const moderationData = {
      userId,
      totalMentionsGiven: statsResult.data.mentionsGiven || 0,
      totalMentionsReceived: statsResult.data.mentionsReceived || 0,
      recentMentionsGiven: 0, // Would need time-based tracking
      averageMentionsPerPost: 0, // Would calculate from post history
      flaggedForExcessiveMentioning: false // Would implement threshold logic
    };
    
    return {
      success: true,
      data: moderationData
    };
    
  } catch (error) {
    console.error('Error getting mention moderation stats:', error);
    return {
      success: false,
      error: error.message || 'Failed to get moderation stats',
      data: null
    };
  }
};

/**
 * Reset user forum statistics (admin only)
 * @param {string} userId - User ID
 * @param {string} adminUserId - Admin user ID
 * @returns {Promise<Object>} - Success status
 */
export const resetUserForumStats = async (userId, adminUserId) => {
  try {
    // TODO: Add admin permission check
    
    const statsRef = doc(db, 'user_forum_stats', userId);
    
    const resetStats = {
      userId,
      questionsPosted: 0,
      repliesPosted: 0,
      totalLikesReceived: 0,
      totalViewsReceived: 0,
      mentionsReceived: 0,
      mentionsGiven: 0,
      resetAt: serverTimestamp(),
      resetBy: adminUserId,
      updatedAt: serverTimestamp()
    };
    
    await setDoc(statsRef, resetStats);
    
    console.log(`Admin ${adminUserId} reset forum stats for user ${userId}`);
    
    return { success: true };
    
  } catch (error) {
    console.error('Error resetting user forum stats:', error);
    return {
      success: false,
      error: error.message || 'Failed to reset forum stats'
    };
  }
};